import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { getEnterpriseStatus, updateEnterpriseStatus, EnterpriseStatus } from '../services/enterpriseApi';
import { dispatchRefreshTokenError } from '../utils/errorHandling';
import { useOrganizationsContext } from '../context/OrganizationsContext';

export const useEnterprise = () => {
  const { getAccessTokenSilently } = useAuth0();
  // Get enterprise status from OrganizationsContext (already fetched in initial-data batch endpoint)
  // This avoids duplicate Auth0 requests
  const { enterprise: orgEnterprise, loading: orgsLoading } = useOrganizationsContext();
  const [enterpriseStatus, setEnterpriseStatus] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Use enterprise from OrganizationsContext if available
  useEffect(() => {
    if (!orgsLoading && orgEnterprise !== undefined) {
      setEnterpriseStatus(orgEnterprise);
      setLoading(false);
    }
  }, [orgEnterprise, orgsLoading]);

  const fetchEnterpriseStatus = async () => {
    // If OrganizationsContext already has enterprise status, use it (no API call needed)
    if (!orgsLoading && orgEnterprise !== undefined) {
      setEnterpriseStatus(orgEnterprise);
      setLoading(false);
      return;
    }

    // Fallback: fetch if OrganizationsContext hasn't loaded yet
    try {
      setLoading(true);
      setError(null);
      
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: 'openid profile email read:organizations roles',
        },
      });
      
      const data = await getEnterpriseStatus(token);
      setEnterpriseStatus(data.enterprise);
    } catch (err: any) {
      // Handle refresh token errors gracefully
      if (err?.message?.includes('Missing Refresh Token')) {
        setError('Session expired. Please log in again.');
        // Notify app shell to prompt logout/login
        dispatchRefreshTokenError();
        // Optionally trigger a re-login
        // loginWithRedirect();
      } else {
        setError(err?.response?.data?.detail || err?.message || 'Failed to fetch enterprise status');
        console.error('Error fetching enterprise status:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateEnterprise = async (enterprise: boolean) => {
    try {
      setError(null);
      
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: 'openid profile email read:organizations roles',
        },
      });
      
      const data = await updateEnterpriseStatus(enterprise, token);
      setEnterpriseStatus(data.enterprise);
      // Broadcast to all tabs/components so they update immediately
      const evt: CustomEvent = new CustomEvent('enterpriseChanged', { detail: { enterprise: data.enterprise } });
      window.dispatchEvent(evt);
      return data;
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Failed to update enterprise status';
      setError(errorMessage);
      console.error('Error updating enterprise status:', err);
      throw err;
    }
  };

  // Don't fetch on mount - use OrganizationsContext instead
  // Only fetch if OrganizationsContext hasn't loaded yet (fallback)
  // Increased timeout to 5 seconds to reduce unnecessary fallback calls
  // Batch endpoint typically loads in ~500ms, so 5s gives plenty of buffer
  useEffect(() => {
    if (orgsLoading) {
      // OrganizationsContext still loading, wait a bit then fetch if needed
      const timeoutId = setTimeout(() => {
        if (orgsLoading) {
          fetchEnterpriseStatus();
        }
      }, 5000);
      return () => clearTimeout(timeoutId);
    }
  }, [orgsLoading]);

  // Listen for global enterprise changes from other components
  useEffect(() => {
    const listener = (e: Event) => {
      const ce = e as CustomEvent<{ enterprise: boolean }>;
      if (typeof ce.detail?.enterprise === 'boolean') {
        setEnterpriseStatus(ce.detail.enterprise);
      }
    };
    window.addEventListener('enterpriseChanged', listener as EventListener);
    return () => window.removeEventListener('enterpriseChanged', listener as EventListener);
  }, []);

  return {
    enterpriseStatus,
    loading,
    error,
    updateEnterprise,
    refetch: fetchEnterpriseStatus,
  };
};
